import React from 'react';
import {
  Avatar,
  AvatarBadge,
  AvatarGroup,
  Button,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from '@cardboard-ui/react';
import { plural, t } from '@lingui/macro';
import { graphql, useMutation } from 'utils/graphClient';
import { OptionsMenu } from 'components/OptionsMenu';
import { OptionsMenuButton } from 'components/OptionsMenu/OptionsMenuButton';
import { CheckIcon, EllipsisHIcon, TrashIcon } from 'components/icons';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { ReminderOption } from 'components/OptionsMenu/options/ReminderOption';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { SignatureIcon } from '../PrepareSignatureFileRenderer/SignatureIcon';
import { useFragment } from 'utils/graphClient';
import {
  ManagersSteps_node$data,
  ManagersSteps_node$key,
} from './__generated__/ManagersSteps_node.graphql';
import { ManagersSteps_deleteSignatureEnvelopeMutation } from './__generated__/ManagersSteps_deleteSignatureEnvelopeMutation.graphql';
import { ManagersSteps_completeSignatureEnvelope_Mutation } from './__generated__/ManagersSteps_completeSignatureEnvelope_Mutation.graphql';
import { SignersDetailsModal } from './SignersDetailsModal';

const nodeFragment = graphql`
  fragment ManagersSteps_node on Node {
    __typename
    id
    ... on SignableInterface {
      signatureEnvelope {
        id
        status
        signatures {
          nodes {
            id
            member {
              id
              name
              avatar {
                url
              }
            }
            status
          }
        }
        canComplete {
          value
        }
        canDelete {
          value
        }
      }
    }
    ...SignersDetailsModal_node
  }
`;

interface ManagersStepsProps {
  node: ManagersSteps_node$key;
}

export const ManagersSteps = ({ node }: ManagersStepsProps) => {
  const signableNode = useFragment(nodeFragment, node);
  const { signatureEnvelope } = signableNode;

  if (!signatureEnvelope) return null;

  const isEnvelopeCompleted = signatureEnvelope.status === 'COMPLETED';
  if (isEnvelopeCompleted) {
    return (
      <HStack>
        <SignatureIcon status="COMPLETED" />
        <Text>
          {t`This document has been signed.`}{' '}
          <DetailsButton signableNode={signableNode} />
        </Text>
      </HStack>
    );
  }

  const envelopeSignatures = signatureEnvelope.signatures.nodes;
  const remainingSigners = envelopeSignatures.reduce(
    (a, s) => (s.status === 'WAITING' ? a + 1 : a),
    0,
  );

  return (
    <HStack>
      <AvatarGroup
        size="sm"
        max={6}
        sx={{
          '.chakra-avatar__excess': {
            fontSize: 'sm',
          },
        }}
      >
        {envelopeSignatures.map((s) => (
          <Avatar
            key={s.id}
            name={s.member.name}
            size="sm"
            src={s.member.avatar?.url}
          >
            {s.status === 'SIGNED' && (
              <AvatarBadge boxSize="1.25em" bg="green.500" color="white">
                <CheckIcon />
              </AvatarBadge>
            )}
          </Avatar>
        ))}
      </AvatarGroup>

      <Text pr={1}>
        {plural(remainingSigners, {
          one: `${remainingSigners} person still needs to sign this document`,
          other: `${remainingSigners} people still need to sign this document`,
        })}{' '}
        <DetailsButton signableNode={signableNode} />
      </Text>

      <SignatureOptionsMenu signableNode={signableNode} />
    </HStack>
  );
};

const deleteSignatureEnvelopeMutation = graphql`
  mutation ManagersSteps_deleteSignatureEnvelopeMutation($id: ID!) {
    deleteSignatureEnvelope(input: { id: $id }) {
      node {
        id
        ...ItemViewer_node
      }
    }
  }
`;

const completeSignatureEnvelopeMutation = graphql`
  mutation ManagersSteps_completeSignatureEnvelope_Mutation(
    $id: ID!
    $knownSignatureCount: Int!
  ) {
    completeSignatureEnvelope(
      input: { id: $id, knownSignatureCount: $knownSignatureCount }
    ) {
      node {
        id
        ...ItemViewer_node
      }
    }
  }
`;

interface SignatureOptionsMenuProps {
  signableNode: ManagersSteps_node$data;
}

const SignatureOptionsMenu = ({ signableNode }: SignatureOptionsMenuProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteConfirmationOpen,
    onOpen: onOpenDeleteConfirmation,
    onClose: onCloseDeleteConfirmation,
  } = useDisclosure();
  const {
    isOpen: isCompleteConfirmationOpen,
    onOpen: onOpenCompleteConfirmation,
    onClose: onCloseCompleteConfirmation,
  } = useDisclosure();

  const [deleteDigitalSignatureEnvelope, deletingDigitalSignatureEnvelope] =
    useMutation<ManagersSteps_deleteSignatureEnvelopeMutation>(
      deleteSignatureEnvelopeMutation,
    );

  const [completeSignatureEnvelope, completingSignatureEnvelope] =
    useMutation<ManagersSteps_completeSignatureEnvelope_Mutation>(
      completeSignatureEnvelopeMutation,
    );

  const { signatureEnvelope } = signableNode;
  if (!signatureEnvelope) return null;

  const handleDeleteSignatureEnvelope = () => {
    deleteDigitalSignatureEnvelope({
      variables: {
        id: signatureEnvelope.id,
      },
    });
  };

  const handleCompleteSignatureEnvelope = () => {
    const completedSignatureCount = signatureEnvelope.signatures.nodes.filter(
      ({ status: signatureStatus }) => signatureStatus !== 'WAITING',
    );

    completeSignatureEnvelope({
      variables: {
        id: signatureEnvelope.id,
        knownSignatureCount: completedSignatureCount.length,
      },
    });
  };

  return (
    <>
      <OptionsMenu
        button={
          <OptionsMenuButton
            aria-label={t`Open signature options`}
            icon={<EllipsisHIcon fontWeight="bold" />}
            size="xs"
            variant="solid"
            colorScheme="gray"
            onClick={onOpen}
          />
        }
        isOpen={isOpen}
        onClose={onClose}
      >
        <ReminderOption nodeId={signableNode.id} />
        {signatureEnvelope.canDelete.value && (
          <OptionsMenuItem
            icon={<TrashIcon />}
            onClick={onOpenDeleteConfirmation}
          >{t`Cancel signatures`}</OptionsMenuItem>
        )}
        {signatureEnvelope.canComplete.value && (
          <OptionsMenuItem
            icon={<SignatureIcon status="COMPLETED" />}
            onClick={onOpenCompleteConfirmation}
          >{t`Complete signatures`}</OptionsMenuItem>
        )}
      </OptionsMenu>
      <ConfirmationDialog
        action={{
          name: t`Cancel all signature requests?`,
          onAction: handleDeleteSignatureEnvelope,
          inProgress: deletingDigitalSignatureEnvelope,
        }}
        isOpen={isDeleteConfirmationOpen}
        onClose={onCloseDeleteConfirmation}
      >
        {t`Notifications will be sent to signers and the signature requester. This can not be undone.`}
      </ConfirmationDialog>
      <ConfirmationDialog
        action={{
          name: t`Confirm Completion of Signature Process?`,
          onAction: handleCompleteSignatureEnvelope,
          inProgress: completingSignatureEnvelope,
        }}
        confirmationText={t`Confirm`}
        isOpen={isCompleteConfirmationOpen}
        onClose={onCloseCompleteConfirmation}
      >
        <Stack>
          <Text>{t`By confirming, you're finalizing the signature process before all signers have completed their signatures.`}</Text>
          <Text>{t`All signers and the original requester will be notified.`}</Text>
          <Text>{t`Please note, this action cannot be reversed.`}</Text>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};

const DetailsButton = ({
  signableNode,
}: {
  signableNode: ManagersSteps_node$data;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Button
        colorScheme="cyan"
        variant="link"
        onClick={onOpen}
      >{t`Details`}</Button>
      <SignersDetailsModal
        isOpen={isOpen}
        onClose={onClose}
        signableNode={signableNode}
      />
    </>
  );
};
